// ./src/components/features/EmbeddingEventTemplates/EmbeddingEventTemplateFormModal/EmbeddingEventTemplateFormModal.tsx
import React, { useState, useEffect } from "react";
import { Stack, Alert, Text, TextInput, Textarea } from "@mantine/core";
import {
  EmbeddingEventTemplate,
  EmbeddingEventTemplateInput,
} from "@/utils/api";
import { AppModal, AppIcon } from "@/components/generic";
import { EmbeddingLayoutTypeSelector } from "@/components/generic/EmbeddingLayoutTypeSelector";

export interface EmbeddingEventTemplateFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Template to edit (if null/undefined, it's create mode) */
  template?: EmbeddingEventTemplate | null;

  /** Callback when template should be created */
  onCreateTemplate?: (
    templateData: EmbeddingEventTemplateInput
  ) => Promise<EmbeddingEventTemplate | null>;

  /** Callback when template should be updated */
  onUpdateTemplate?: (
    templateId: string,
    templateData: EmbeddingEventTemplateInput
  ) => Promise<EmbeddingEventTemplate | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from template operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface TemplateFormData {
  name: string;
  description: string;
  templateHtml: string;
  layoutType: string;
  previewImageUrl: string | null | undefined;
  status: number;
}

const initialFormData: TemplateFormData = {
  name: "",
  description: "",
  templateHtml: "",
  layoutType: "grid",
  previewImageUrl: "",
  status: 100,
};

export const EmbeddingEventTemplateFormModal: React.FC<
  EmbeddingEventTemplateFormModalProps
> = ({
  opened,
  onClose,
  template,
  onCreateTemplate,
  onUpdateTemplate,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<TemplateFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});

  const isEditMode = Boolean(template) && Boolean(template?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or template changes
  useEffect(() => {
    if (opened) {
      if (template) {
        // Edit mode - populate form with template data
        setFormData({
          name: template.name || "",
          description: template.description || "",
          templateHtml: template.templateHtml || "",
          layoutType: template.layoutType || "grid",
          previewImageUrl: template.previewImageUrl || "",
          status: template.status != null ? template.status : 100,
        });
      } else {
        // Create mode - use defaults
        setFormData(initialFormData);
      }

      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, template, onClearError]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Template name is required";
    }

    if (!formData.templateHtml.trim()) {
      newErrors.templateHtml = "Template HTML is required";
    }

    if (!formData.layoutType) {
      newErrors.layoutType = "Layout type is required";
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field changes
  const handleFieldChange = (field: keyof TemplateFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare template data
      const templateData: EmbeddingEventTemplateInput = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        templateHtml: formData.templateHtml.trim(),
        layoutType: formData.layoutType,
        previewImageUrl: formData.previewImageUrl?.trim() || null,
        status: formData.status,
      };

      let result: EmbeddingEventTemplate | null = null;

      if (isEditMode && template && onUpdateTemplate) {
        // Update existing template
        result = await onUpdateTemplate(template.id || "", templateData);
      } else if (!isEditMode && onCreateTemplate) {
        // Create new template
        result = await onCreateTemplate(templateData);
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Get the error to display for a field
  const getDisplayError = (field: string): string | undefined => {
    return validationErrors[field];
  };

  // Render generic errors
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Template" : "Create New Template"}
      size="xl"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Save",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        {/* Template Name */}
        <TextInput
          label="Template Name"
          placeholder="Enter template name"
          value={formData.name}
          onChange={(e) => handleFieldChange("name", e.target.value)}
          error={getDisplayError("name")}
          required
          withAsterisk
          size="md"
        />

        {/* Description */}
        <Textarea
          label="Description"
          placeholder="Enter template description (optional)"
          value={formData.description}
          onChange={(e) => handleFieldChange("description", e.target.value)}
          error={getDisplayError("description")}
          minRows={2}
          maxRows={4}
          autosize
          size="md"
        />

        {/* Layout Type */}
        <EmbeddingLayoutTypeSelector
          label="Layout Type"
          value={formData.layoutType}
          onChange={(value) => handleFieldChange("layoutType", value)}
          error={getDisplayError("layoutType")}
          required
          withAsterisk
        />

        {/* Preview Image URL */}
        {/* <TextInput
              label="Preview Image URL"
              placeholder="Enter preview image URL (optional)"
              value={formData.previewImageUrl}
              onChange={(e) =>
                handleFieldChange("previewImageUrl", e.target.value)
              }
              error={getDisplayError("previewImageUrl")}
            /> */}

        {/* Template HTML */}
        <Textarea
          label="Template HTML"
          placeholder="Enter HTML template code"
          value={formData.templateHtml}
          onChange={(e) => handleFieldChange("templateHtml", e.target.value)}
          error={getDisplayError("templateHtml")}
          required
          withAsterisk
          minRows={10}
          maxRows={20}
          autosize
          size="md"
          styles={{
            input: {
              fontFamily: "monospace",
              fontSize: "15px",
            },
          }}
        />
      </Stack>

      {/* Error display */}
      {renderGenericErrors()}
    </AppModal>
  );
};
